import type { GridExceljsProcessInput } from "@mui/x-data-grid-premium";

/** The analysis values written to a sheet; one line per plot or per row. */
export interface AnalysisExcelRow {
  emergenceCount: number | null;
  plot: string;
  qtRow: number | null;
  range: number | null;
  row: number | null;
  rowCover: number | null;
  variety: string;
}

interface SheetColumn {
  header: string;
  key: keyof AnalysisExcelRow;
  numFmt?: string;
  width: number;
}

const PLOT_COLUMN: SheetColumn = { header: "Plot", key: "plot", width: 12 };
const ROW_COLUMN: SheetColumn = { header: "Row", key: "row", width: 8 };
const SHARED_COLUMNS: SheetColumn[] = [
  { header: "Variety", key: "variety", width: 18 },
  { header: "Range", key: "range", width: 8 },
  { header: "QT Row", key: "qtRow", width: 8 },
  { header: "Emergence Count", key: "emergenceCount", width: 18 },
  { header: "Row Cover (%)", key: "rowCover", numFmt: "0.0", width: 14 },
];

const PLOT_SHEET_COLUMNS = [PLOT_COLUMN, ...SHARED_COLUMNS];
const ROW_SHEET_COLUMNS = [PLOT_COLUMN, ROW_COLUMN, ...SHARED_COLUMNS];

const addSheet = (
  workbook: GridExceljsProcessInput["workbook"],
  name: string,
  columns: readonly SheetColumn[],
  rows: readonly AnalysisExcelRow[],
): void => {
  const sheet = workbook.addWorksheet(name);
  sheet.columns = columns.map(({ header, key, numFmt, width }) => ({
    header,
    key,
    style: numFmt == null ? undefined : { numFmt },
    width,
  }));
  sheet.addRows([...rows]);
  sheet.getRow(1).font = { bold: true };
  sheet.views = [{ state: "frozen", ySplit: 1 }];
};

/**
 * `exceljsPostProcess` hook for the grid's Excel export. The grid writes only
 * the rows currently displayed, so its sheet is dropped and replaced with a
 * "Plots" sheet (one line per plot) and a "Rows" sheet (one line per crop row).
 * The two sheets share the `Plot` column as a key. Because they are built from
 * the data and not the grid, the file is the same whichever view is showing and
 * whatever sorting or filtering is applied.
 */
export const replaceWithPlotAndRowSheets = (
  { workbook, worksheet }: GridExceljsProcessInput,
  data: {
    plotRows: readonly AnalysisExcelRow[];
    rowRows: readonly AnalysisExcelRow[];
  },
): void => {
  workbook.removeWorksheet(worksheet.id);
  addSheet(workbook, "Plots", PLOT_SHEET_COLUMNS, data.plotRows);
  addSheet(workbook, "Rows", ROW_SHEET_COLUMNS, data.rowRows);
};
